import { useCallback, useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Pointer leaving the (i) or the tip: this long before it closes, so the pointer can cross the gap. */
export const INFO_TIP_CLOSE_DELAY_MS = 180;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])';

export interface InfoTipProps {
  /** The whole tip as plain text: the trigger's accessible name. */
  label: string;
  /** The tip itself (bold, italics, line breaks, bullets, a link). */
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
}

const isMouse = (event: PointerEvent) => event.pointerType === 'mouse';

/**
 * The (i) beside a setting (F22, R24, R28): a Radix Popover, placed in JS and
 * kept inside the window (`collisionPadding`), so it is never cut off.
 *
 * - A mouse over the (i), or focus on it, opens it without taking focus. It
 *   stays open while the pointer is over the tip, so a link inside it can be
 *   clicked; leaving closes it after `INFO_TIP_CLOSE_DELAY_MS`.
 * - A click, a tap, Enter or Space pins it open (a second click closes it).
 *   From the keyboard, focus moves to the tip's link when it has one.
 * - Escape closes it wherever focus is, and brings focus back to the (i) when
 *   it was inside the tip.
 * - The trigger's `aria-label` is the plain text, so a screen reader reads the
 *   tip without opening it.
 * - Clicks and keys stop at the tip, so an (i) inside a table row never opens
 *   or closes the row.
 */
export function InfoTip({ label, children, side = 'top', align = 'center', className }: InfoTipProps) {
  const [open, setOpenState] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  const pinnedRef = useRef(false);
  // Set by Enter or Space: move focus into the tip once it is open.
  const focusInsideRef = useRef(false);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const setOpen = useCallback(
    (next: boolean) => {
      cancelClose();
      if (!next) pinnedRef.current = false;
      setOpenState(next);
    },
    [cancelClose],
  );
  const scheduleClose = useCallback(() => {
    if (pinnedRef.current) return;
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setOpenState(false);
    }, INFO_TIP_CLOSE_DELAY_MS);
  }, [cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  const focusFirstInside = () => {
    contentRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Anchor asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label={label}
          aria-expanded={open}
          className={cn(
            'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full align-middle text-muted-foreground',
            'transition-colors duration-150 ease-out hover:text-foreground motion-reduce:transition-none',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
          onPointerEnter={(event) => {
            if (isMouse(event)) setOpen(true);
          }}
          onPointerLeave={(event) => {
            if (isMouse(event)) scheduleClose();
          }}
          onFocus={() => {
            cancelClose();
            setOpenState(true);
          }}
          onBlur={(event) => {
            if (contentRef.current?.contains(event.relatedTarget as Node | null)) return;
            pinnedRef.current = false;
            scheduleClose();
          }}
          onClick={(event) => {
            event.stopPropagation();
            const keyboard = event.detail === 0;
            if (keyboard) {
              pinnedRef.current = true;
              if (open) focusFirstInside();
              else {
                focusInsideRef.current = true;
                setOpen(true);
                pinnedRef.current = true;
              }
              return;
            }
            if (open && pinnedRef.current) {
              setOpen(false);
              return;
            }
            setOpen(true);
            pinnedRef.current = true;
          }}
          onKeyDown={(event) => {
            // Enter and Space belong to the (i), never the row around it.
            if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
          }}
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={contentRef}
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'z-50 w-max max-w-[min(280px,calc(100vw-16px))] rounded-md border bg-popover px-3 py-2 text-xs leading-relaxed text-popover-foreground shadow-md outline-none',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            'motion-reduce:animate-none',
          )}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            if (focusInsideRef.current) {
              focusInsideRef.current = false;
              // After Radix has placed the tip.
              window.requestAnimationFrame(focusFirstInside);
            }
          }}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={() => {
            if (contentRef.current?.contains(document.activeElement)) triggerRef.current?.focus();
          }}
          onInteractOutside={(event) => {
            // The (i) is not "outside": its own handlers decide.
            if (triggerRef.current?.contains(event.target as Node)) event.preventDefault();
          }}
          onPointerEnter={(event) => {
            if (isMouse(event)) cancelClose();
          }}
          onPointerLeave={(event) => {
            if (isMouse(event)) scheduleClose();
          }}
          onBlur={(event) => {
            const next = event.relatedTarget as Node | null;
            if (contentRef.current?.contains(next) || triggerRef.current?.contains(next)) return;
            pinnedRef.current = false;
            scheduleClose();
          }}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
